// Archivo: src/pages/home-landing/components/RaffleInfoSection.jsx

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../supabaseClient'; // 👈 Importamos el cliente de Supabase

// ***************************************************************
// MODIFICACIÓN: Eliminamos la prop raffleData y manejamos la carga internamente
// ***************************************************************
const RaffleInfoSection = ({ onVerifyTicket, onContactClick, className = '' }) => {
  const navigate = useNavigate(); // 👈 Hook para navegación
  const [raffleData, setRaffleData] = useState(null); // 👈 Estado para los datos del sorteo
  const [loading, setLoading] = useState(true); // 👈 Estado de carga
  const [error, setError] = useState(null); // 👈 Estado de error

  // Efecto para cargar los datos del sorteo activo al montar el componente
  useEffect(() => {
    const fetchRaffleData = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('raffles') // 👈 Nombre exacto de tu tabla en Supabase
          .select('*')
          .eq('estado', 'activo') // 👈 Solo traemos el sorteo activo
          .single(); // 👈 Esperamos un solo resultado

        if (error) throw error;

        setRaffleData(data);
      } catch (err) {
        console.error('Error al cargar los datos del sorteo:', err);
        setError(err.message || 'Error desconocido');
      } finally {
        setLoading(false);
      }
    };

    fetchRaffleData();
  }, []); // 👈 Se ejecuta solo una vez al montar

  // Si hay error, mostramos un mensaje
  if (error) {
    return (
      <div className={`bg-muted/10 py-16 text-center ${className}`}>
        <p className="text-xl text-error">⚠️ Error al cargar la información del sorteo.</p>
        <p className="mt-2 text-sm text-gray-600">{error}</p>
      </div>
    );
  }

  // Si está cargando, mostramos un placeholder
  if (loading) {
    return (
      <div className={`bg-muted/10 py-16 text-center ${className}`}>
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-secondary mx-auto"></div>
        <p className="mt-4 text-lg">Cargando información del sorteo...</p>
      </div>
    );
  }

  // Si no hay datos (no hay sorteo activo), mostramos un mensaje
  if (!raffleData) {
    return (
      <div className={`bg-muted/10 py-16 text-center ${className}`}>
        <p className="text-xl text-warning">ℹ️ No hay ningún sorteo activo en este momento.</p>
      </div>
    );
  }

  // 3. DATOS DINÁMICOS (ahora vienen de Supabase)
  const staticDetails = {
    description: `El Gran Sorteo ${raffleData?.titulo || 'Áurea Fortuna'} es nuestra lotería premium más esperada del año. Con premios que cambian vidas y un sistema de sorteo completamente transparente y verificable.\n\nCada boleto te da la oportunidad de ganar uno de nuestros increíbles premios, como ${raffleData?.premio_principal || 'el gran premio'}.`,
    features: [
      {
        icon: 'Shield',
        title: 'Sorteo Verificable',
        description: 'Sistema transparente con verificación pública del sorteo'
      },
      {
        icon: 'Clock',
        title: 'Resultados Inmediatos',
        description: 'Conoce los resultados en tiempo real el día del sorteo'
      },
      {
        icon: 'Award',
        title: 'Múltiples Premios',
        description: 'Más oportunidades de ganar con premios secundarios'
      },
      {
        icon: 'CreditCard',
        title: 'Pagos Seguros',
        description: 'Múltiples métodos de pago seguros y verificados'
      }
    ],

    terms: [
      'Debe ser mayor de 18 años para participar',
      'Un boleto por cédula de identidad',
      // INYECTAMOS LA FECHA DINÁMICA DESDE SUPABASE
      `El sorteo se realizará el ${raffleData?.fecha_sorteo ? new Date(raffleData.fecha_sorteo).toLocaleDateString('es-VE', { day: 'numeric', month: 'long', year: 'numeric' }) : 'Próximamente'} a las 10:00 PM`,
      'Los ganadores serán contactados por teléfono y WhatsApp',
      'Los premios deben ser reclamados dentro de 30 días'
    ],

    images: [
      {
        src: raffleData?.imagen_url || "https://images.unsplash.com/photo-1594686900103-3c9698dbb31b",
        alt: "Happy winner holding lottery ticket with big smile celebrating victory"
      },
      {
        src: "https://images.unsplash.com/photo-1518688248740-7c31f1a945c4",
        alt: "Golden lottery balls in transparent machine during draw process"
      }
    ]
  };

  const raffleDetails = staticDetails;

  return (
    // El fondo principal de la sección es oscuro (bg-muted/10)
    <div className={`bg-muted/10 py-12 md:py-20 lg:py-24 ${className}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Section Header */}
        <div className="text-center mb-12 md:mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground tracking-tight">
            Todo lo que Necesitas Saber
          </h2>
          <p className="mt-4 max-w-2xl mx-auto text-lg text-text-secondary font-body">
            Conoce todos los detalles sobre nuestro sorteo premium y cómo participar
          </p>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
          
          {/* Left Column - Description & Features (FONDO BLANCO: bg-card) */}
          <div className="lg:col-span-2 space-y-8">
            
            {/* Description Card */}
            <div className="bg-card rounded-xl shadow-lg p-6 md:p-8">
              {/* CORRECCIÓN DE CONTRASTE: text-card-foreground -> text-black */}
              <h3 className="flex items-center text-xl md:text-2xl font-heading font-bold text-black mb-4">
                <Icon name="Sparkles" size={24} className="text-secondary mr-3" />
                <span>Descripción del Sorteo: {raffleData?.titulo}</span>
              </h3>
              
              <div className="prose prose-sm max-w-none">
                {raffleDetails?.description?.split('\n\n')?.map((paragraph, index) =>
                // CORRECCIÓN CLAVE: text-muted-foreground -> text-black
                <p key={index} className="text-black font-body leading-relaxed">
                    {paragraph}
                  </p>
                )}
              </div>
            </div>

            {/* Features Grid */}
            <div className="bg-card rounded-xl shadow-lg p-6 md:p-8">
              {/* CORRECCIÓN DE CONTRASTE: text-card-foreground -> text-black */}
              <h3 className="flex items-center text-xl md:text-2xl font-heading font-bold text-black mb-6">
                <Icon name="Star" size={24} className="text-secondary mr-3" />
                <span>Características Destacadas</span>
              </h3>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-8">
                {raffleDetails?.features?.map((feature, index) =>
                <div key={index} className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <Icon name={feature?.icon} size={24} className="text-secondary" />
                    </div>
                    <div>
                      {/* CORRECCIÓN DE CONTRASTE: text-card-foreground -> text-black */}
                      <h4 className="font-body font-semibold text-black">
                        {feature?.title}
                      </h4>
                      {/* CORRECCIÓN DE CONTRASTE: text-muted-foreground -> text-black/70 */}
                      <p className="text-sm text-black/70 font-caption mt-1">
                        {feature?.description}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Actions (FONDO BLANCO: bg-card) */}
          <div className="lg:col-span-1 bg-card rounded-xl shadow-lg p-6 md:p-8 sticky top-8">
            {/* CORRECCIÓN DE CONTRASTE: text-card-foreground -> text-black */}
            <h3 className="flex items-center text-xl font-heading font-bold text-black mb-6">
              <Icon name="Zap" size={24} className="text-secondary mr-3" />
              <span>Acciones Rápidas</span>
            </h3>
            
            <div className="space-y-4">
              {/* ACCIONES RÁPIDAS MODIFICADAS - Eliminamos "Contactar Soporte" */}
              
              {/* Botón 1: Principal - Gris oscuro */}
              <Button
                variant="custom"
                fullWidth
                onClick={onVerifyTicket}
                iconName="Search"
                iconPosition="left"
                className="flex items-center justify-center py-3 px-4 bg-gray-800 text-white rounded-lg font-semibold transition-all duration-300 ease-in-out hover:bg-gray-700 hover:shadow-lg transform hover:-translate-y-0.5"
              >
                Verificar mi Boleto
              </Button>
              
              {/* ❌ BOTÓN "CONTACTAR SOPORTE" ELIMINADO - Líneas 209-219 */}
              
              {/* Botón 2: WhatsApp - Verde */}
              <Button
                variant="custom"
                fullWidth
                onClick={() => window.open('https://wa.me/5804120357042', '_blank')}
                iconName="Phone"
                iconPosition="left"
                className="flex items-center justify-center py-3 px-4 bg-green-500 text-white rounded-lg font-semibold transition-all duration-300 ease-in-out hover:bg-green-600 hover:shadow-lg transform hover:-translate-y-0.5"
              >
                WhatsApp Directo
              </Button>

              {/* Botón 3: Comprar Boletos - Gradiente */}
              <Button
                variant="custom"
                fullWidth
                onClick={() => {
                  // 🔗 REDIRECCIÓN CORREGIDA: Usando navigate() en lugar de window.location.href
                  navigate('/purchase-flow');
                }}
                iconName="ShoppingCart"
                iconPosition="left"
                className="flex items-center justify-center py-3 px-4 bg-gradient-to-r from-secondary to-amber-500 text-gray-900 rounded-lg font-semibold transition-all duration-300 ease-in-out hover:from-secondary/90 hover:to-amber-400 hover:shadow-lg transform hover:-translate-y-0.5"
              >
                Comprar Boletos Ahora
              </Button>
            </div>
          </div>
        </div>

        {/* Terms & Conditions */}
        <div className="lg:col-span-3 bg-card rounded-xl shadow-lg p-6 md:p-8 mt-8">
          {/* CORRECCIÓN DE CONTRASTE: text-card-foreground -> text-black */}
          <h3 className="flex items-center text-xl md:text-2xl font-heading font-bold text-black mb-6">
            <Icon name="AlertCircle" size={24} className="text-secondary mr-3" />
            <span>Términos y Condiciones Importantes</span>
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
            <ul className="space-y-3">
              {raffleDetails?.terms?.slice(0, 3)?.map((term, index) => ( // Sintaxis correcta
              <li key={index} className="flex items-start">
                  <Icon name="CheckCircle" size={16} className="text-success mt-0.5 mr-3 flex-shrink-0" />
                  {/* CORRECCIÓN DE CONTRASTE: text-muted-foreground -> text-black */}
                  <span className="text-sm text-black font-caption">
                    {term}
                  </span>
                </li>
              ))}
            </ul>
            
            <ul className="space-y-3">
              {raffleDetails?.terms?.slice(3)?.map((term, index) => ( // Sintaxis correcta
              <li key={index} className="flex items-start">
                  <Icon name="CheckCircle" size={16} className="text-success mt-0.5 mr-3 flex-shrink-0" />
                  {/* CORRECCIÓN DE CONTRASTE: text-muted-foreground -> text-black */}
                  <span className="text-sm text-black font-caption">
                    {term}
                  </span>
                </li>
              ))}
            </ul>
          </div>
          
          <div className="mt-6 pt-4 border-t border-border">
            {/* CORRECCIÓN DE CONTRASTE: text-muted-foreground -> text-black/70 */}
            <p className="text-xs text-black/70 font-caption text-center">
              Al participar en este sorteo, aceptas automáticamente todos los términos y condiciones. 
              Para más información, consulta nuestros términos completos o contacta a nuestro equipo de soporte.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RaffleInfoSection;